// Display · Icon
//
// Central icon library wrapper. All icon imports are locked behind this
// component — swapping the underlying library is one-file work (Rule 5 / wrap
// rule #4: "We import its companion library directly").
//
// Current backend: inline SVG paths (Lucide-style stroked icons). No dependency
// on @ant-design/icons for the supported set — the paths are embedded to avoid
// a secondary bundle chunk.
//
// API deviates from the source:
//   - `size` is now a semantic scale ("xs" | "sm" | "md" | "lg") not a raw px.
//   - `tone` replaces `color`: accepts ToneName (maps to TONE[tone].text) or
//     `"current"` to inherit the surrounding text color (CSS currentColor).
//   - `strokeWidth` is NOT exposed — callers do not tune it.
//
// All visual values come from `@ds/tokens/palette`.

import { FONT_SIZE, TONE, type ToneName } from "@ds/tokens/palette";

// ─── Supported icon names ─────────────────────────────────────────────────────
//
// Extend this union when a new icon is needed. Every name must have a matching
// case in the switch below. Keep the names in the same order as the switch.

export type IconName =
  | "inbox"
  | "file"
  | "fileDone"
  | "bank"
  | "swap"
  | "swapHoriz"
  | "building"
  | "settings"
  | "lightning"
  | "search"
  | "download"
  | "linkCopy"
  | "edit"
  | "more"
  | "shop"
  | "cube"
  | "check"
  | "paperclip"
  | "user"
  | "phone"
  | "mail"
  | "history"
  | "matched"
  | "approved"
  | "uploadCloud"
  | "back"
  | "arrowRight"
  | "arrowLeft"
  | "lock"
  | "chevronDown"
  | "truck"
  | "scale"
  | "pin"
  | "checkCircle"
  | "xCircle"
  | "link"
  | "thumbsUp"
  | "editPen"
  | "moreHorizontal"
  | "creditCard"
  | "mapPin"
  | "fileText"
  | "upload"
  | "filePlus"
  | "close"
  | "warning"
  | "external"
  | "delete"
  | "plus"
  | "copy";

// ─── Size scale ───────────────────────────────────────────────────────────────
//
// Mapped to FONT_SIZE values so icons sit optically on the same cap-height band
// as adjacent text.

/** Icon pixel size per scale name.
 *  xs → FONT_SIZE.micro (10), sm → FONT_SIZE.label (11), md → FONT_SIZE.normal (14), lg → FONT_SIZE.subheading (16).
 *  Values intentionally map to existing palette tokens — no new additions needed. */
const ICON_SIZE_PX: Record<IconSize, number> = {
  xs: FONT_SIZE.micro,       // 10 — inline micro context
  sm: FONT_SIZE.label,       // 11 — dense chip / table cell
  md: FONT_SIZE.normal,      // 14 — default, body text
  lg: FONT_SIZE.subheading,  // 16 — section headers, hero bands
};

// ─── Inputs ───────────────────────────────────────────────────────────────────

export type IconSize = "xs" | "sm" | "md" | "lg";
export type IconTone = ToneName | "current";

export interface IconInput {
  /** Supported icon identifier. Required — the icon is the primary signal. */
  name: IconName;
  /**
   * Size scale.
   * - `"xs"` — 10 px (inline micro context).
   * - `"sm"` — 11 px (chip / table cell).
   * - `"md"` (default) — 14 px (body text).
   * - `"lg"` — 16 px (section header / hero).
   */
  size?: IconSize;
  /**
   * Tone.
   * - A `ToneName` maps to `TONE[tone].text` for legible color-on-light.
   * - `"current"` (default) inherits the surrounding text color via CSS
   *   `currentColor` — useful when the icon is inside a toned container.
   */
  tone?: IconTone;
  /** `data-testid` on the root `<svg>`. Atom — no inner testids auto-emitted. */
  testId?: string;
}

// Atom: no Output, no Actions — interfaces dropped per @ds Rule 3.

export type IconProps = IconInput;

// ─── Implementation ───────────────────────────────────────────────────────────

export function Icon({
  name,
  size = "md",
  tone = "current",
  testId,
}: IconProps) {
  const px    = ICON_SIZE_PX[size];
  const color = tone === "current" ? "currentColor" : TONE[tone].text;

  const common = {
    width:           px,
    height:          px,
    viewBox:         "0 0 24 24" as string,
    fill:            "none" as const,
    stroke:          color,
    strokeWidth:     1.7,
    strokeLinecap:   "round" as const,
    strokeLinejoin:  "round" as const,
  };

  const svgProps = {
    ...common,
    "data-testid": testId,
    // inline-flex baseline alignment
    style:         { display: "inline-block", verticalAlign: "middle", flexShrink: 0 },
  };

  switch (name) {
    case "inbox":
      return (
        <svg {...svgProps}>
          <path d="M22 12h-6l-2 3h-4l-2-3H2" />
          <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
        </svg>
      );
    case "file":
      return (
        <svg {...svgProps}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
      );
    case "fileDone":
      return (
        <svg {...svgProps}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M9 14l2 2 4-4" />
        </svg>
      );
    case "bank":
      return (
        <svg {...svgProps}>
          <path d="M3 21h18" />
          <path d="M3 10h18" />
          <path d="M5 6l7-3 7 3" />
          <path d="M4 10v11" />
          <path d="M20 10v11" />
          <path d="M8 14v3" />
          <path d="M12 14v3" />
          <path d="M16 14v3" />
        </svg>
      );
    case "swap":
      return (
        <svg {...svgProps}>
          <path d="M16 3l4 4-4 4" />
          <path d="M20 7H4" />
          <path d="M8 21l-4-4 4-4" />
          <path d="M4 17h16" />
        </svg>
      );
    case "swapHoriz":
      return (
        <svg {...svgProps}>
          <path d="M7 16h13" />
          <path d="M16 12l4 4-4 4" />
          <path d="M17 8H4" />
          <path d="M8 4L4 8l4 4" />
        </svg>
      );
    case "building":
      return (
        <svg {...svgProps}>
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <path d="M9 9h.01M13 9h.01M9 13h.01M13 13h.01M9 17h.01M13 17h.01" />
        </svg>
      );
    case "settings":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1v0z" />
        </svg>
      );
    case "lightning":
      return (
        <svg {...svgProps}>
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      );
    case "search":
      return (
        <svg {...svgProps}>
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
      );
    case "download":
      return (
        <svg {...svgProps}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <path d="M7 10l5 5 5-5" />
          <path d="M12 15V3" />
        </svg>
      );
    case "linkCopy":
      return (
        <svg {...svgProps}>
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
      );
    case "edit":
      return (
        <svg {...svgProps}>
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      );
    case "more":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      );
    case "shop":
      return (
        <svg {...svgProps}>
          <path d="M3 9l1-5h16l1 5" />
          <path d="M3 9v11a1 1 0 001 1h16a1 1 0 001-1V9" />
          <path d="M3 9h18" />
          <path d="M9 21V13h6v8" />
        </svg>
      );
    case "cube":
      return (
        <svg {...svgProps}>
          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
          <path d="M3.27 6.96L12 12l8.73-5.04" />
          <path d="M12 22V12" />
        </svg>
      );
    case "check":
      return (
        <svg {...svgProps}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case "paperclip":
      return (
        <svg {...svgProps}>
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
      );
    case "user":
      return (
        <svg {...svgProps}>
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "phone":
      return (
        <svg {...svgProps}>
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.12.91.33 1.8.62 2.65a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.43-1.19a2 2 0 012.11-.45c.85.29 1.74.5 2.65.62A2 2 0 0122 16.92z" />
        </svg>
      );
    case "mail":
      return (
        <svg {...svgProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case "history":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "matched":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l2 2 6-6" />
          <path d="M16 16l-2-2" />
        </svg>
      );
    case "approved":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      );
    case "uploadCloud":
      return (
        <svg {...svgProps}>
          <path d="M16 16l-4-4-4 4" />
          <path d="M12 12v9" />
          <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
          <path d="M16 16l-4-4-4 4" />
        </svg>
      );
    case "back":
      return (
        <svg {...svgProps}>
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      );
    case "arrowRight":
      return (
        <svg {...svgProps}>
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      );
    case "arrowLeft":
      return (
        <svg {...svgProps}>
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
      );
    case "lock":
      return (
        <svg {...svgProps}>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      );
    case "chevronDown":
      return (
        <svg {...svgProps}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    case "truck":
      return (
        <svg {...svgProps}>
          <rect x="1" y="6" width="14" height="11" rx="1" />
          <path d="M15 9h4l3 3v5h-7" />
          <circle cx="6" cy="19" r="2" />
          <circle cx="18" cy="19" r="2" />
        </svg>
      );
    case "scale":
      return (
        <svg {...svgProps}>
          <path d="M3 21h18" />
          <path d="M12 3v18" />
          <path d="M5 11l-2-5h6l-2 5a3 3 0 002 0z" />
          <path d="M19 11l-2-5h6l-2 5a3 3 0 002 0z" />
        </svg>
      );
    case "pin":
      return (
        <svg {...svgProps}>
          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "checkCircle":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "xCircle":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
          <path d="M15 9l-6 6M9 9l6 6" />
        </svg>
      );
    case "link":
      return (
        <svg {...svgProps}>
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
      );
    case "thumbsUp":
      return (
        <svg {...svgProps}>
          <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3z" />
          <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
        </svg>
      );
    case "editPen":
      return (
        <svg {...svgProps}>
          <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5z" />
        </svg>
      );
    case "moreHorizontal":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      );
    case "creditCard":
      return (
        <svg {...svgProps}>
          <rect x="1" y="4" width="22" height="16" rx="2" />
          <path d="M1 10h22" />
        </svg>
      );
    case "mapPin":
      return (
        <svg {...svgProps}>
          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "fileText":
      return (
        <svg {...svgProps}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M16 13H8M16 17H8M10 9H8" />
        </svg>
      );
    case "upload":
      return (
        <svg {...svgProps}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <path d="M17 8l-5-5-5 5" />
          <path d="M12 3v12" />
        </svg>
      );
    case "filePlus":
      return (
        <svg {...svgProps}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M12 18v-6M9 15h6" />
        </svg>
      );
    case "close":
      return (
        <svg {...svgProps}>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    case "warning":
      return (
        <svg {...svgProps}>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
      );
    case "external":
      return (
        <svg {...svgProps}>
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
          <path d="M15 3h6v6" />
          <path d="M10 14L21 3" />
        </svg>
      );
    case "delete":
      return (
        <svg {...svgProps}>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      );
    case "plus":
      return (
        <svg {...svgProps}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "copy":
      return (
        <svg {...svgProps}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      );
  }
}
